import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { animate, displayStyle, ease } from '../board/ui';
import { glowTexture } from '../road/glow';

/** The gem toast's soft blue, so the cell and the toast read as one. */
const GEM_BLUE = 0x8fd8ff;
/** A deeper blue for the frame's outer light, which shows on pale reels. */
const GEM_DEEP = 0x2f8fe0;
/** How long a gem cell's moment lasts: about as long as its gems fly. */
export const GEM_GLOW_MS = 1400;

/** A gem cell to light: its centre and size in the layer's space, and its gems. */
export interface GemCellSpot {
  x: number;
  y: number;
  size: number;
  gems: number;
}

/** The gem cells' moment as plain data, for browser tests. */
export interface GemCellGlowSnapshot {
  /** Moments played since the slot opened. */
  plays: number;
  /** Cells lit now. */
  glowing: number;
  /** The "+N" over each cell lit now. */
  labels: string[];
  /** The "+N" over each cell of the last moment played, lit or not: a slow
   *  test browser can miss the moment while it is lit. */
  lastLabels: string[];
}

interface Piece {
  root: Container;
  glow: Sprite;
  frame: Graphics;
  label: Text;
  size: number;
}

/**
 * The gem cells' moment on the reels: each gem cell lights up, a blue frame
 * and a soft glow round it, and its gems ("+10") pop up over it and drift up,
 * while the gems fly to the wallet's gem count. The frame reads on pale reels
 * and dark ones alike. Only alpha, scale and position move, so it stays cheap
 * on low-end phones. Takes no taps; the next spin clears it, so it never
 * holds the reels up.
 */
export class GemCellGlow extends Container {
  private plays = 0;
  private lastLabels: string[] = [];
  private live: Piece[] = [];

  constructor() {
    super();
    this.eventMode = 'none';
  }

  get snapshot(): GemCellGlowSnapshot {
    return {
      plays: this.plays,
      glowing: this.live.length,
      labels: this.live.map((p) => p.label.text),
      lastLabels: [...this.lastLabels],
    };
  }

  /** Light these cells; resolves when the moment is over. */
  async play(spots: readonly GemCellSpot[]): Promise<void> {
    if (spots.length === 0) return;
    this.plays += 1;
    this.lastLabels = spots.map((spot) => `+${spot.gems}`);
    const pieces = spots.map((spot) => this.build(spot));
    this.live.push(...pieces);
    await animate(GEM_GLOW_MS, (t) => {
      for (const p of pieces) {
        if (p.root.destroyed) continue;
        // Up fast, a gentle shimmer, then out.
        const up = Math.min(1, t / 0.1);
        const out = t > 0.72 ? 1 - (t - 0.72) / 0.28 : 1;
        const shimmer = 0.85 + 0.15 * Math.sin(t * Math.PI * 7);
        p.glow.alpha = up * out * shimmer;
        p.glow.width = p.glow.height = p.size * (1.1 + 0.35 * ease.outCubic(up));
        p.frame.alpha = up * out;
        p.frame.scale.set(1.12 - 0.12 * ease.outBack(up));
        // The "+N": pops in, rises above the cell and fades with the glow.
        const pop = t < 0.14 ? 0.5 + 0.6 * ease.outBack(t / 0.14) : t < 0.24 ? 1.1 - 0.1 * ((t - 0.14) / 0.1) : 1;
        p.label.scale.set(Math.min(1.1, pop));
        p.label.alpha = Math.min(1, t / 0.08) * out;
        p.label.y = -p.size * (0.1 + 0.42 * ease.outCubic(t));
      }
    });
    for (const p of pieces) this.drop(p);
  }

  /** Put out every cell still lit (a new spin is starting). */
  clear(): void {
    for (const p of [...this.live]) this.drop(p);
  }

  private build(spot: GemCellSpot): Piece {
    const root = new Container();
    root.position.set(spot.x, spot.y);
    const glow = new Sprite(glowTexture());
    glow.anchor.set(0.5);
    glow.tint = GEM_BLUE;
    glow.blendMode = 'add';
    glow.alpha = 0;
    // The frame: a deep blue light outside, a bright line, a white edge in.
    const half = spot.size * 0.47;
    const r = spot.size * 0.12;
    const line = Math.max(2, spot.size * 0.035);
    const frame = new Graphics()
      .roundRect(-half, -half, half * 2, half * 2, r)
      .stroke({ color: GEM_DEEP, alpha: 0.35, width: line * 4 })
      .roundRect(-half, -half, half * 2, half * 2, r)
      .stroke({ color: GEM_BLUE, alpha: 0.95, width: line * 1.6 })
      .roundRect(-half, -half, half * 2, half * 2, r)
      .stroke({ color: 0xffffff, alpha: 0.9, width: line * 0.6 });
    frame.alpha = 0;
    const size = Math.max(24, Math.round(spot.size * 0.36));
    const label = new Text({ text: `+${spot.gems}`, style: displayStyle(Math.min(42, size), GEM_BLUE) });
    label.anchor.set(0.5);
    label.alpha = 0;
    root.addChild(glow, frame, label);
    this.addChild(root);
    return { root, glow, frame, label, size: spot.size };
  }

  private drop(piece: Piece): void {
    this.live = this.live.filter((p) => p !== piece);
    if (!piece.root.destroyed) piece.root.destroy({ children: true });
  }
}
